class RelatedColorSwatches extends HTMLElement {
    // Debounce handle for pointerenter image swap, so a quick sweep
    // across swatches doesn't force a full-res image load per swatch.
    #hoverTimeout = null;

    connectedCallback() {
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('pointerenter', this.#handlePointerEnter, true);
        this.addEventListener('pointerleave', this.#handlePointerLeave, true);
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.#handleClick);
        this.removeEventListener('pointerenter', this.#handlePointerEnter, true);
        this.removeEventListener('pointerleave', this.#handlePointerLeave, true);
        clearTimeout(this.#hoverTimeout);
    }

    /** @returns {HTMLElement | null} */
    get #card() {
        return this.closest('product-card');
    }

    #handleClick = async (event) => {
        const swatch = event.target.closest('.related-color-swatches__swatch');
        if (!(swatch instanceof HTMLButtonElement)) return;
        if (swatch.getAttribute('aria-pressed') === 'true') return;

        event.preventDefault();
        event.stopPropagation();

        this.#updateActiveState(swatch);
        this.#updateImages(swatch);
        this.#updateLinks(swatch); // must run before #updateQuickAdd
        this.#updateBadges(swatch);
        this.#updateQuickAddVisibility(swatch);
        await this.#updateQuickAdd(swatch);
    };

    #handlePointerEnter = (event) => {
        const swatch = event.target.closest('.related-color-swatches__swatch');
        if (!(swatch instanceof HTMLButtonElement)) return;

        clearTimeout(this.#hoverTimeout);
        this.#hoverTimeout = setTimeout(() => {
            this.#updateImages(swatch);
        }, 100);
    };

    #handlePointerLeave = (event) => {
        const swatch = event.target.closest('.related-color-swatches__swatch');
        if (!(swatch instanceof HTMLButtonElement)) return;

        clearTimeout(this.#hoverTimeout);

        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && swatch.contains(nextTarget)) return;
        this.#restoreActiveImages();
    };

    /** @param {HTMLButtonElement} swatch */
    #updateActiveState(swatch) {
        for (const btn of this.querySelectorAll('.related-color-swatches__swatch')) {
            const isTarget = btn === swatch;
            btn.setAttribute('aria-pressed', isTarget ? 'true' : 'false');
            btn.classList.toggle('related-color-item--current', isTarget);
        }
    }

    /** @param {HTMLButtonElement} swatch */
    #updateImages(swatch) {
        const card = this.#card;
        if (!card) return;

        const primaryUrl = swatch.dataset.productImage;
        const secondaryUrl = swatch.dataset.productImageSecondary;
        const slideshow = card.querySelector('slideshow-component');
        const slides = card.querySelectorAll('slideshow-slide');
        const primaryImg = slides[0]?.querySelector('img.product-media__image');
        const secondaryImg = slides[1]?.querySelector('img.product-media__image');

        if (primaryImg instanceof HTMLImageElement && primaryUrl) {
            primaryImg.src = primaryUrl;
            primaryImg.srcset = primaryUrl;
            primaryImg.removeAttribute('loading');
        }
        if (secondaryImg instanceof HTMLImageElement && secondaryUrl) {
            secondaryImg.src = secondaryUrl;
            secondaryImg.srcset = secondaryUrl;
            secondaryImg.removeAttribute('loading');
        }

        if (slideshow && typeof slideshow.select === 'function') {
            const initialSlide = slideshow.initialSlide;
            const initialSlideId = initialSlide?.getAttribute('slide-id');

            if (initialSlideId) {
                slideshow.select({ id: initialSlideId }, undefined, { animate: false });
            } else if (slides.length > 0) {
                slideshow.select(0, undefined, { animate: false });
            }
        }
    }

    #restoreActiveImages() {
        const activeSwatch = this.querySelector('.related-color-swatches__swatch[aria-pressed="true"]');
        if (activeSwatch instanceof HTMLButtonElement) {
            this.#updateImages(activeSwatch);
        }
    }

    /** @param {HTMLButtonElement} swatch */
    #updateLinks(swatch) {
        const card = this.#card;
        if (!card) return;

        const productUrl = swatch.dataset.productUrl;
        if (!productUrl) return;

        const productCardLink = card.querySelector('[ref="productCardLink"]');
        if (productCardLink instanceof HTMLAnchorElement) {
            productCardLink.href = productUrl;
        }

        const cardLinkWrapper = card.closest('product-card-link');
        if (cardLinkWrapper) {
            const productImage = swatch.dataset.productImage;
            if (productImage) cardLinkWrapper.setAttribute('data-featured-media-url', productImage);
            const productId = swatch.dataset.productId;
            if (productId) cardLinkWrapper.setAttribute('data-product-id', productId);
        }
    }

    /** @param {HTMLButtonElement} swatch */
    #updateBadges(swatch) {
        const card = this.#card;
        if (!card) return;

        const isSoldOut = swatch.dataset.badgeSoldOut === 'true';
        const isSale = swatch.dataset.badgeSale === 'true';

        const badgesContainer = card.querySelector('.product-badges');
        if (!badgesContainer) return;

        let badge = badgesContainer.querySelector('.product-badges__badge');

        if (!isSoldOut && !isSale) {
            if (badge) badge.remove();
            return;
        }

        if (!badge) {
            badge = document.createElement('div');
            badge.className = 'product-badges__badge product-badges__badge--rectangle';
            badgesContainer.appendChild(badge);
        }

        if (isSoldOut) {
            badge.className = 'product-badges__badge product-badges__badge--rectangle color-sold-out';
            badge.textContent = badgesContainer.dataset.soldOutLabel || 'Sold out';
        } else if (isSale) {
            badge.className = 'product-badges__badge product-badges__badge--rectangle color-sale';
            badge.textContent = badgesContainer.dataset.saleLabel || 'Sale';
        }
    }

    /** @param {HTMLButtonElement} swatch */
    #updateQuickAddVisibility(swatch) {
        const card = this.#card;
        if (!card) return;

        const content = card.querySelector('.product-card__content');
        if (!content) return;

        content.style.setProperty('--quick-add-display', swatch.dataset.quickAdd === 'true' ? 'flex' : 'none');
        content.style.setProperty('--quick-add-mobile-display', swatch.dataset.quickAddMobile === 'true' ? 'flex' : 'none');
    }

    /** @param {HTMLButtonElement} swatch */
    async #updateQuickAdd(swatch) {
        const card = this.#card;
        if (!card) return;

        const quickAdd = /** @type {any} */ (card.querySelector('quick-add-component'));
        if (!quickAdd) return;

        const productUrl = swatch.dataset.productUrl;
        if (!productUrl) return;

        // QuickAddComponent.productPageUrl reads from the card link href.
        // #updateLinks already set that to productUrl, so the getter
        // returns the correct URL. fetchProductPage pre-warms the cache
        // so the modal opens instantly with the right product content.
        if (typeof quickAdd.fetchProductPage === 'function') {
            await quickAdd.fetchProductPage(productUrl);
        }
    }
}

if (!customElements.get('related-color-swatches')) {
    customElements.define('related-color-swatches', RelatedColorSwatches);
}

const PRODUCT_COLOR_NAVIGATION_KEY = 'noosh:product-color-navigation';
const PRODUCT_COLOR_NAVIGATION_MAX_AGE = 15_000;
const PRODUCT_COLOR_TRANSITION_TYPE = 'product-color-transition';
const PRODUCT_COLOR_LINK_SELECTOR =
    'variant-picker[data-template-product-match="true"] a.related-color-item[href]';

document.addEventListener('click', handleProductColorNavigation);
window.addEventListener('pageshow', clearStaleProductColorNavigationState);
restoreProductColorNavigation();

/** @param {MouseEvent} event */
function handleProductColorNavigation(event) {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        !(event.target instanceof Element)
    ) {
        return;
    }

    const link = event.target.closest(PRODUCT_COLOR_LINK_SELECTOR);
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;
    if (link.getAttribute('aria-current') === 'page') {
        event.preventDefault();
        return;
    }

    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin) return;
    if (destination.pathname === window.location.pathname && destination.search === window.location.search) return;

    const { top: viewportTop } = link.getBoundingClientRect();
    const navigationState = {
        destinationPath: destination.pathname,
        productId: link.dataset.relatedProductId || '',
        viewportTop,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        timestamp: Date.now(),
    };

    try {
        sessionStorage.setItem(PRODUCT_COLOR_NAVIGATION_KEY, JSON.stringify(navigationState));
    } catch (error) {
        console.warn('[product-color-navigation] could not save scroll position', error);
    }

    link.setAttribute('aria-busy', 'true');
    link.setAttribute('data-view-transition-triggered', 'true');
    link.setAttribute('data-view-transition-type', PRODUCT_COLOR_TRANSITION_TYPE);
}

function clearStaleProductColorNavigationState() {
    for (const link of document.querySelectorAll(`${PRODUCT_COLOR_LINK_SELECTOR}[aria-busy="true"]`)) {
        link.removeAttribute('aria-busy');
        link.removeAttribute('data-view-transition-triggered');
        link.removeAttribute('data-view-transition-type');
    }
}

function restoreProductColorNavigation() {
    let navigationState;

    try {
        navigationState = JSON.parse(sessionStorage.getItem(PRODUCT_COLOR_NAVIGATION_KEY) || 'null');
    } catch (error) {
        console.warn('[product-color-navigation] could not read scroll position', error);
        sessionStorage.removeItem(PRODUCT_COLOR_NAVIGATION_KEY);
        return;
    }

    const isValidNavigation =
        navigationState &&
        navigationState.destinationPath === window.location.pathname &&
        Date.now() - navigationState.timestamp <= PRODUCT_COLOR_NAVIGATION_MAX_AGE;

    if (!isValidNavigation) {
        sessionStorage.removeItem(PRODUCT_COLOR_NAVIGATION_KEY);
        return;
    }

    // Consume this state once so a refresh or a later visit does not reuse it.
    sessionStorage.removeItem(PRODUCT_COLOR_NAVIGATION_KEY);

    const previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';

    let animationFrame;
    let resizeObserver;
    let hasFinished = false;

    const restore = () => {
        if (hasFinished) return;

        const currentSwatch = Array.from(document.querySelectorAll(PRODUCT_COLOR_LINK_SELECTOR)).find(
            (link) =>
                link instanceof HTMLAnchorElement &&
                link.classList.contains('related-color-item--current') &&
                link.dataset.relatedProductId === navigationState.productId
        );

        const targetScrollY = currentSwatch
            ? window.scrollY + currentSwatch.getBoundingClientRect().top - navigationState.viewportTop
            : navigationState.scrollY;

        window.scrollTo({
            top: Math.max(0, targetScrollY),
            left: navigationState.scrollX,
            behavior: 'instant',
        });
    };

    const scheduleRestore = () => {
        if (hasFinished) return;
        cancelAnimationFrame(animationFrame);
        animationFrame = requestAnimationFrame(() => {
            animationFrame = requestAnimationFrame(restore);
        });
    };

    const finish = () => {
        if (hasFinished) return;
        hasFinished = true;
        cancelAnimationFrame(animationFrame);
        resizeObserver?.disconnect();
        window.removeEventListener('load', scheduleRestore);
        history.scrollRestoration = previousScrollRestoration;

        for (const eventName of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
            window.removeEventListener(eventName, finish);
        }
    };

    const start = () => {
        restore();
        scheduleRestore();

        const mainContent = document.getElementById('MainContent');
        if (mainContent && 'ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(scheduleRestore);
            resizeObserver.observe(mainContent);
        }

        for (const eventName of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
            window.addEventListener(eventName, finish, { passive: true });
        }

        window.addEventListener('load', scheduleRestore, { once: true });
        window.setTimeout(finish, 1_500);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
        start();
    }
}